import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { ProductCard } from '../components/common/ProductCard';
import { BrandCard } from '../components/common/BrandCard';
import { LeadFormDialog } from '../components/common/LeadFormDialog';
import { getCategory, getProducts, getBrands } from '../lib/api';
import { generateWhatsAppLink, getCategoryWhatsAppMessage } from '../lib/utils';
import { MessageCircle } from 'lucide-react';

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const catRes = await getCategory(categorySlug);
        setCategory(catRes.data);

        const [prodRes, brandsRes] = await Promise.all([
          getProducts({ category_id: catRes.data.id }),
          getBrands(catRes.data.id),
        ]);

        setProducts(prodRes.data);
        setBrands(brandsRes.data);
        
        // Get all brands for product cards
        const allBrandsRes = await getBrands();
        setAllBrands(allBrandsRes.data);
      } catch (error) {
        console.error('Error fetching category:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categorySlug]);

  if (loading) {
    return (
      <div className="pt-32 min-h-screen bg-[#F7F6F3]">
        <div className="container-custom">
          <div className="skeleton h-64 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="skeleton h-72" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="pt-32 min-h-screen">
        <div className="container-custom text-center py-20">
          <h1 className="heading-lg mb-4">Category Not Found</h1>
          <Link to="/products" className="btn-primary">View All Products</Link>
        </div>
      </div>
    );
  }

  const whatsappMessage = getCategoryWhatsAppMessage(category.name);
  const whatsappLink = generateWhatsAppLink(whatsappMessage);

  return (
    <>
      <SEOHead
        title={`${category.name} - Premium ${category.name} Wholesale`}
        description={category.meta_description || `Shop premium ${category.name.toLowerCase()} from top brands. Wholesale pricing available for retailers and resellers.`}
        canonical={`https://passionofaroma.com/category/${category.slug}`}
        ogImage={category.image}
      />

      {/* Hero */}
      <section 
        className="pt-32 pb-16 bg-[#0B1F3B] relative"
        data-testid="category-hero"
      >
        {category.image && (
          <>
            <div className="absolute inset-0 opacity-20">
              <img src={category.image} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3B] to-transparent" />
          </>
        )}
        <div className="container-custom relative">
          <Breadcrumbs items={[
            { label: 'Products', href: '/products' },
            { label: category.name },
          ]} />
          <div className="mt-6 max-w-2xl">
            <span className="label-text mb-4 block">Collection</span>
            <h1 className="heading-xl text-white mb-4">{category.name}</h1>
            {category.description && (
              <p className="text-gray-300 text-lg mb-6">{category.description}</p>
            )}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setShowQuoteForm(true)}
                className="btn-gold"
                data-testid="category-quote-btn"
              >
                Request Wholesale Quote
              </button>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary border-white text-white hover:bg-white hover:text-[#0B1F3B] flex items-center gap-2"
                data-testid="category-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp Enquiry
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Brands in Category */}
      {brands.length > 0 && (
        <section className="py-12 bg-white" data-testid="category-brands">
          <div className="container-custom">
            <h2 className="heading-sm mb-6">Brands in {category.name}</h2>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {brands.slice(0, 12).map((brand) => (
                <BrandCard 
                  key={brand.id} 
                  brand={brand} 
                  categorySlug={category.slug}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Products */}
      <section className="py-16 bg-[#F7F6F3]" data-testid="category-products">
        <div className="container-custom">
          <div className="flex justify-between items-center mb-8">
            <h2 className="heading-md">{category.name} Products</h2>
            <span className="text-gray-500">{products.length} products</span>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {products.map((product) => {
                const brand = allBrands.find(b => b.id === product.brand_id);
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    category={category.name}
                    categorySlug={category.slug}
                    brand={brand?.name}
                    brandSlug={brand?.slug}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white">
              <p className="text-gray-500 mb-4">No products found in this category yet.</p>
              <button onClick={() => setShowQuoteForm(true)} className="btn-primary">
                Request Quote for {category.name}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white" data-testid="category-cta">
        <div className="container-custom text-center">
          <h2 className="heading-md mb-4">Need More Options?</h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            We have access to hundreds more {category.name.toLowerCase()} products. 
            Contact us with your requirements.
          </p>
          <button 
            onClick={() => setShowQuoteForm(true)}
            className="btn-primary"
          >
            Request Custom Quote
          </button>
        </div>
      </section>

      <LeadFormDialog
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        sourcePageUrl={`/category/${category.slug}`}
        interestCategoryId={category.id}
        showBuyerType={true}
        title={`Request ${category.name} Quote`}
        subtitle={`Get wholesale pricing for our ${category.name.toLowerCase()} collection`}
      />
    </>
  );
}
